import React from 'react';
import { capitalize } from 'lodash';
import { useTranslation } from 'react-i18next';
import { ParseKeys, Namespace } from 'i18next';
import { Location } from 'icons';
import {
  practitionerQuery_practitioner as Practitioner,
  practitionerQuery_practitioner_details_specialities_areasOfExpertise as AreaOfExpertise,
  practitionerQuery_practitioner_details_specialities_approaches as Approach,
} from 'Services/BeGateway/Practitioner/__generated__/practitionerQuery';
import {
  getAboutMeQuestions,
  getLocation,
  getProfileSummary,
  Profession,
} from 'Book/shared';
import useFeatureFlag, { FEATURE_FLAGS } from 'flags/useFeatureFlag';

import { parseBeGatewayId } from 'utils/relay';
import { getISOLanguageFromCode } from 'utils/getISOLanguageFromCode';

import { CollapsibleAnswer, PillList, ProfileAttributes } from './components';
import {
  Container,
  PractitionerTitleText,
  PractitionerNameText,
  SummaryText,
  StyledIcon,
  DimmedText,
  ItemRow,
  AboutMeContainer,
  Subheader,
} from './styles';
import { valueOrTranslation } from './utils';

type PractitionerProfileDetailsProps = {
  practitioner: Practitioner;
};

export default ({ practitioner }: PractitionerProfileDetailsProps) => {
  const { t } = useTranslation('book', { keyPrefix: 'practitioner' });
  const showGenderAndEthnicity = useFeatureFlag(
    FEATURE_FLAGS.TALK_GENDER_AND_ETHNICITY,
  );
  const { t: translateLanguageName } =
    useTranslation<Namespace<'languages'>>('languages');
  const translateLanguageCode = (code: string) => {
    const { en: languageName } = getISOLanguageFromCode(code);

    return `${translateLanguageName(
      languageName as ParseKeys<Namespace<'languages'>>,
    )}`;
  };

  const {
    city,
    state,
    country,
    firstName,
    lastName,
    languages: languageResponse,
    details,
    gender,
    ethnicity,
  } = practitioner;

  // As languages is a json array, empty responses are returned as empty strings
  const languages = languageResponse === '' ? null : languageResponse;
  const languageLabels: string[] = languages?.map(translateLanguageCode) ?? [];

  const {
    profileQuestionResponses,
    type: practitionerType,
    specialities,
    profileInformation,
  } = details || {};

  const profileSummary = getProfileSummary(
    profileQuestionResponses,
    practitionerType ?? '',
  );
  const aboutMeSections = getAboutMeQuestions(profileQuestionResponses);
  const profession = profileInformation?.profession as Profession;
  const professionValue =
    Profession[profession] && !(profession === Profession.other)
      ? t(`professions.${profession}`)
      : null;

  return (
    <Container>
      <PractitionerNameText>{`${firstName} ${lastName}`}</PractitionerNameText>
      <PractitionerTitleText>
        {professionValue || capitalize(practitionerType)}
      </PractitionerTitleText>
      {showGenderAndEthnicity ? (
        <ProfileAttributes
          country={country.name}
          languages={languageLabels}
          gender={gender}
          ethnicity={ethnicity}
        />
      ) : (
        (city || state || country) && (
          <ItemRow>
            <StyledIcon Icon={Location} />
            <DimmedText>{getLocation(practitioner)}</DimmedText>
          </ItemRow>
        )
      )}
      <SummaryText>{profileSummary}</SummaryText>

      {!showGenderAndEthnicity && (
        <PillList title={t('profile.languages')} labels={languageLabels} />
      )}

      <PillList
        title={t('profile.focusAreas')}
        labels={specialities?.areasOfExpertise
          ?.filter((area): area is AreaOfExpertise => area !== null)
          .map(area =>
            valueOrTranslation(
              'practitioner.specialities',
              area.translationKey,
            ),
          )}
      />

      <PillList
        title={t('profile.approaches')}
        labels={specialities?.approaches
          ?.filter((approach): approach is Approach => approach !== null)
          .map(approach =>
            valueOrTranslation(
              'practitioner.specialities',
              approach.translationKey,
            ),
          )}
      />

      {!!aboutMeSections?.length && (
        <>
          <Subheader>{t('profile.aboutMe')}</Subheader>
          <AboutMeContainer>
            {[...aboutMeSections]
              .sort(
                (a, b) =>
                  (parseBeGatewayId(a.profileQuestion.id) as number) -
                  (parseBeGatewayId(b.profileQuestion.id) as number),
              )
              .map(q => (
                <CollapsibleAnswer key={q.id} questionAnswer={q} />
              ))}
          </AboutMeContainer>
        </>
      )}
    </Container>
  );
};
